/**
 * Keeps the tablet and the server in step whenever there is data: uploads the records waiting on
 * the tablet (only those not uploaded yet: new sign-ins, cycles, events), and downloads only what
 * changed in what it works from when there is none (wards, operators' PIN checks, the switch's
 * password). An idle sync moves a few hundred bytes.
 */
import type {
  OfflinePackResponse,
  PackChangesResponse,
  SyncRequest,
  SyncResponse,
} from '@evesix/contracts';
import { ApiRequestError, type ApiClient } from './api';
import type { LocalStore, OutboxEntry } from './local-store';
import { applyChanges, fromFullPack, needsFullCopy, type TabletPack } from './pack';

const TOKEN_KEY = 'tablet.token';
const PACK_KEY = 'tablet.pack';
const ROBOT_KEY = 'tablet.robot';

/** The robot this tablet was set up for, and its hospital once the pack has arrived. */
export interface TabletRobot {
  readonly id: string;
  readonly hospitalName: string | null;
}

export interface SyncStatus {
  /** The last sync reached the server. */
  readonly online: boolean;
  /** Records waiting on the tablet for the server. */
  readonly pending: number;
  /** Records the server refused; kept for a person to look at. */
  readonly rejected: number;
  readonly lastSyncAt: number | null;
  readonly enrolled: boolean;
  /** Null until the tablet is set up for a robot. */
  readonly robot: TabletRobot | null;
}

export interface SyncServiceOptions {
  readonly now?: () => number;
  /** Records per upload; well inside the API's limits. */
  readonly chunk?: number;
}

export class SyncService {
  readonly #store: LocalStore;
  /** Carries this tablet's own credentials (`Authorization: Tablet …`). */
  readonly #tabletClient: ApiClient;
  readonly #now: () => number;
  readonly #chunk: number;
  readonly #listeners = new Set<() => void>();
  readonly ready: Promise<void>;
  #pack: TabletPack | null = null;
  #token: string | null = null;
  #robotId: string | null = null;
  #running: Promise<boolean> | null = null;
  #again: Promise<boolean> | null = null;
  #status: SyncStatus = {
    online: false,
    pending: 0,
    rejected: 0,
    lastSyncAt: null,
    enrolled: false,
    robot: null,
  };

  constructor(
    store: LocalStore,
    tabletClient: ApiClient,
    { now = Date.now, chunk = 50 }: SyncServiceOptions = {},
  ) {
    this.#store = store;
    this.#tabletClient = tabletClient;
    this.#now = now;
    this.#chunk = chunk;
    this.ready = this.#load();
  }

  async #load(): Promise<void> {
    this.#token = (await this.#store.get<string>(TOKEN_KEY)) ?? null;
    const saved = await this.#store.get<TabletPack | OfflinePackResponse>(PACK_KEY);
    // Saved by app 0.3 as the whole pack: kept, and replaced by a full copy at the next sync.
    this.#pack = saved && 'sanitisedBeds' in saved ? fromFullPack(saved, 0) : (saved ?? null);
    this.#robotId = (await this.#store.get<string>(ROBOT_KEY)) ?? this.#pack?.robot.id ?? null;
    this.#tabletClient.setToken(this.#token);
    await this.#recount();
  }

  /** What the tablet works from with no data; null until it has synced once. */
  get pack(): TabletPack | null {
    return this.#pack;
  }

  get enrolled(): boolean {
    return this.#token !== null;
  }

  /** The robot this tablet drives; null until it is set up. */
  get robotId(): string | null {
    return this.#robotId;
  }

  status(): SyncStatus {
    return this.#status;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /**
   * Takes on the robot this tablet was just set up for (its credentials from POST /tablets/setup),
   * then fetches its pack.
   */
  async adopt(token: string, robotId: string): Promise<void> {
    await this.ready;
    await this.#store.put(TOKEN_KEY, token);
    await this.#store.put(ROBOT_KEY, robotId);
    this.#token = token;
    this.#robotId = robotId;
    this.#tabletClient.setToken(token);
    await this.syncNow();
  }

  /**
   * Gives up the robot ("Reset tablet"): the server stops taking this tablet's token, and the
   * tablet forgets the robot, its pack and its credentials. Throws without data. Records still
   * waiting must be uploaded first; the caller checks.
   */
  async release(): Promise<void> {
    await this.ready;
    await this.#tabletClient.delete('/tablets/current');
    await Promise.all([TOKEN_KEY, ROBOT_KEY, PACK_KEY].map((key) => this.#store.remove(key)));
    this.#token = null;
    this.#robotId = null;
    this.#pack = null;
    this.#tabletClient.setToken(null);
    await this.#recount(false);
  }

  /**
   * Uploads what is waiting and refreshes the pack. Never runs twice at once: a call during a sync
   * gets one more sync straight after it, so a record made (or data regained) meanwhile is not
   * left until the next interval.
   */
  syncNow(): Promise<boolean> {
    if (this.#running) {
      this.#again ??= this.#running.then(() => {
        this.#again = null;
        return this.syncNow();
      });
      return this.#again;
    }
    this.#running = this.#sync().finally(() => {
      this.#running = null;
    });
    return this.#running;
  }

  /**
   * Syncs every `intervalMs`, every `retryMs` while records wait and the server cannot be reached,
   * and as soon as the network comes back. A SIM out of data usually still shows as connected, so
   * the browser's `online` event cannot be relied on to say the data is back. Returns a stop
   * function.
   */
  start(intervalMs = 60_000, retryMs = 15_000): () => void {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const tick = async () => {
      await this.syncNow().catch(() => false); // the loop must outlive one failed attempt
      if (stopped) return;
      const waiting = !this.#status.online && this.#status.pending > 0;
      clearTimeout(timer);
      timer = setTimeout(() => void tick(), waiting ? retryMs : intervalMs);
    };
    const onOnline = () => void tick();
    globalThis.addEventListener?.('online', onOnline);
    void this.ready.then(tick);
    return () => {
      stopped = true;
      clearTimeout(timer);
      globalThis.removeEventListener?.('online', onOnline);
    };
  }

  async #sync(): Promise<boolean> {
    await this.ready;
    if (!this.#token) return false;
    let online = false;
    try {
      const pending = (await this.#store.outbox()).filter((e) => e.state === 'pending');
      for (let i = 0; i < pending.length; i += this.#chunk) {
        await this.#upload(pending.slice(i, i + this.#chunk));
      }
      this.#pack = await this.#download();
      await this.#store.put(PACK_KEY, this.#pack);
      online = true;
    } catch {
      // No data, or the server is unreachable: everything stays on the tablet for next time.
    } finally {
      await this.#recount(online);
    }
    return online;
  }

  /** Only what changed since the last sync; a full copy the first time and once a day. */
  async #download(): Promise<TabletPack> {
    const now = this.#now();
    const base = needsFullCopy(this.#pack, now) ? null : this.#pack;
    const since = base?.cursor ? `?since=${encodeURIComponent(base.cursor)}` : '';
    try {
      const changes = await this.#tabletClient.get<PackChangesResponse>(
        `/tablets/current/changes${since}`,
      );
      return applyChanges(base, changes, now);
    } catch (error) {
      // An API from before change tracking: the whole pack, as app 0.3 synced.
      if (!(error instanceof ApiRequestError) || error.status !== 404) throw error;
      const pack = await this.#tabletClient.get<OfflinePackResponse>('/tablets/current/pack');
      return fromFullPack(pack, now);
    }
  }

  async #upload(entries: readonly OutboxEntry[]): Promise<void> {
    const of = (kind: OutboxEntry['kind']) =>
      entries.filter((e) => e.kind === kind).map((e) => e.body);
    const request = {
      clientTime: new Date(this.#now()).toISOString(),
      sessions: of('session'),
      cycles: of('cycle'),
      events: of('event'),
    } as unknown as SyncRequest;
    let response: SyncResponse;
    try {
      response = await this.#tabletClient.post<SyncResponse>('/tablets/current/sync', request);
    } catch (error) {
      if (!(error instanceof ApiRequestError) || error.status !== 400) throw error;
      // One malformed record must not hold back every other: find it, set it aside.
      if (entries.length > 1) {
        for (const entry of entries) await this.#upload([entry]);
        return;
      }
      for (const entry of entries) {
        await this.#store.addToOutbox({ ...entry, state: 'rejected', reason: error.message });
      }
      return;
    }
    await this.#store.removeFromOutbox(response.accepted);
    for (const { id, reason } of response.rejected) {
      const entry = entries.find((e) => e.id === id);
      if (entry) await this.#store.addToOutbox({ ...entry, state: 'rejected', reason });
    }
  }

  async #recount(online = this.#status.online): Promise<void> {
    const entries = await this.#store.outbox();
    this.#status = {
      online,
      pending: entries.filter((e) => e.state === 'pending').length,
      rejected: entries.filter((e) => e.state === 'rejected').length,
      lastSyncAt: online ? this.#now() : this.#status.lastSyncAt,
      enrolled: this.#token !== null,
      robot: this.#robotId
        ? { id: this.#robotId, hospitalName: this.#pack?.robot.hospitalName ?? null }
        : null,
    };
    for (const listener of this.#listeners) listener();
  }
}
